import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Stack,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import CustomPagination from 'app/widgets/Shared/Pagination';
import superadminServices from 'app/services/superadmin-services';
import RegisterPhysicalDeviceDialog from './RegisterPhysicalDeviceDialog';
import {
  CreateButton,
  SaveButton,
  StandardFiltersBar,
  StandardPageHeader,
  StandardSearchField,
  ActionIconButton,
  ResponsiveDataTable,
} from 'app/shared/ui';

const EMPTY_FORM = {
  id: null,
  company_id: '',
  store_id: '',
  store: null,
  name: '',
  code: '',
  kind: '',
  zone: '',
  device_type_id: '',
  simulated: false,
};

const DATA_FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'orphan_devices', label: 'Dispositivos huérfanos' },
  { value: 'without_metrics', label: 'Dispositivos sin datos' },
  { value: 'unregistered_metrics', label: 'Datos sin dispositivo registrado' },
];

const generateDeviceCode = (bytes = 12) => {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const randomBytes = new Uint8Array(bytes);
    window.crypto.getRandomValues(randomBytes);
    return Array.from(randomBytes, (value) => value.toString(16).padStart(2, '0')).join('');
  }

  // Fallback for environments without crypto API.
  return `${Date.now().toString(16)}${Math.random().toString(16).replace('0.', '')}`
    .slice(0, bytes * 2)
    .padEnd(bytes * 2, '0');
};

const DevicesIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const [records, setRecords] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editOptions, setEditOptions] = useState({ stores: [], device_types: [], zones: [], kinds: [] });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(searchParams.get('company_id') || '');
  const [selectedDataFilter, setSelectedDataFilter] = useState(searchParams.get('data_filter') || '');

  const [openForm, setOpenForm] = useState(false);
  const [openRegisterPhysical, setOpenRegisterPhysical] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadingEditOptions, setLoadingEditOptions] = useState(false);
  const [editOptionsReady, setEditOptionsReady] = useState(false);

  const params = useMemo(() => {
    const parts = [
      `page=${currentPage}`,
      'include=company,store,device_type',
      encodeURI('q[s][]=updated_at desc'),
    ];

    if (searchQuery.trim()) {
      parts.push(encodeURI(`q[name_or_code_or_kind_or_zone_cont]=${searchQuery.trim()}`));
    }

    if (selectedCompanyId) {
      parts.push(encodeURI(`q[company_id_eq]=${selectedCompanyId}`));
    }

    if (selectedDataFilter) {
      parts.push(`data_filter=${selectedDataFilter}`);
    }

    return `?${parts.join('&')}`;
  }, [currentPage, searchQuery, selectedCompanyId, selectedDataFilter]);

  const loadCompanies = () => {
    superadminServices.getCompanies('?page=1&include_archived=false')
      .then((resp) => setCompanies(resp?.data || []))
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo cargar empresas.' }));
  };

  const loadRecords = () => {
    setLoading(true);
    superadminServices.getDevices(params)
      .then((resp) => {
        setRecords(resp?.data || []);
        setTotalPages(resp?.meta?.total_pages || 1);
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo cargar dispositivos.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    const companyIdFromQuery = searchParams.get('company_id') || '';
    const dataFilterFromQuery = searchParams.get('data_filter') || '';
    setSelectedCompanyId((currentValue) => (`${currentValue}` === `${companyIdFromQuery}` ? currentValue : companyIdFromQuery));
    setSelectedDataFilter((currentValue) => (`${currentValue}` === `${dataFilterFromQuery}` ? currentValue : dataFilterFromQuery));
  }, [searchParams]);

  useEffect(() => {
    loadRecords();
  }, [params]);

  const loadEditOptions = (companyId, deviceId = null) => {
    setLoadingEditOptions(true);
    setEditOptionsReady(false);
    superadminServices.getDeviceEditOptions({ companyId, deviceId })
      .then((resp) => setEditOptions(resp || { stores: [], device_types: [], zones: [], kinds: [] }))
      .catch(() => setEditOptions({ stores: [], device_types: [], zones: [], kinds: [] }))
      .finally(() => {
        setLoadingEditOptions(false);
        setEditOptionsReady(true);
      });
  };

  const openCreate = () => {
    const initialCompany = selectedCompanyId || companies?.[0]?.id || '';
    setForm({ ...EMPTY_FORM, company_id: initialCompany, code: generateDeviceCode() });
    setOpenForm(true);
  };

  const openEdit = (record) => {
    const next = {
      id: record.id,
      company_id: record.company_id || record?.company?.id || '',
      store_id: record.store_id || record?.store?.id || '',
      store: record?.store || null,
      name: record.name || '',
      code: record.code || '',
      kind: record.kind || '',
      zone: record.zone || '',
      device_type_id: record.device_type_id || record?.device_type?.id || '',
      simulated: !!record.simulated,
    };

    setForm(next);
    setOpenForm(true);
  };

  useEffect(() => {
    if (!openForm) return;
    loadEditOptions(form.company_id || null, form.id || null);
  }, [openForm, form.company_id, form.id]);

  useEffect(() => {
    if (!openForm || !form.store_id || loadingEditOptions || !editOptionsReady) return;
    const storeStillAvailable = _.some(editOptions?.stores || [], (store) => `${store.id}` === `${form.store_id}`);
    if (!storeStillAvailable) {
      setForm((prev) => ({ ...prev, store_id: '', store: null }));
    }
  }, [openForm, form.store_id, form.company_id, editOptions?.stores, loadingEditOptions, editOptionsReady]);

  const saveRecord = () => {
    if (!form.company_id || !form.name?.trim() || !form.code?.trim() || !form.device_type_id) {
      setNotice({ severity: 'error', text: 'Completa empresa, nombre, código y tipo de sensor.' });
      return;
    }

    setSaving(true);
    const payload = {
      device: {
        company_id: form.company_id,
        store_id: form.store_id || null,
        name: form.name,
        code: form.code,
        kind: form.kind,
        zone: form.zone,
        device_type_id: form.device_type_id,
        simulated: form.simulated,
      },
    };

    const request = form.id
      ? superadminServices.updateDevice(form.id, payload, '?include=company,store,device_type')
      : superadminServices.createDevice(payload, '?include=company,store,device_type');

    request
      .then(() => {
        setNotice({ severity: 'success', text: form.id ? 'Dispositivo actualizado.' : 'Dispositivo creado.' });
        setOpenForm(false);
        loadRecords();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo guardar el dispositivo.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setSaving(false));
  };

  const destroyRecord = (record) => {
    if (!window.confirm(`¿Eliminar dispositivo ${record.name || record.code}?`)) return;

    superadminServices.deleteDevice(record.id)
      .then(() => {
        setNotice({ severity: 'success', text: 'Dispositivo eliminado.' });
        loadRecords();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo eliminar el dispositivo.';
        setNotice({ severity: 'error', text: msg });
      });
  };

  const selectedCompany = _.find(companies, (company) => `${company.id}` === `${selectedCompanyId}`) || null;

  const selectedStore = _.find(editOptions?.stores || [], (store) => `${store.id}` === `${form.store_id}`) || form.store || null;
  const selectedDeviceType = _.find(editOptions?.device_types || [], (type) => `${type.id}` === `${form.device_type_id}`) || null;

  const zones = _.uniq(_.compact(editOptions?.zones || []));
  const kinds = _.uniq(_.compact(editOptions?.kinds || []));

  const renderRowActions = (record) => (
    record?.platform_status === 'unregistered_metrics' ? null :
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      <ActionIconButton size="small" title="Editar dispositivo" onClick={() => openEdit(record)}>
        <EditOutlinedIcon fontSize="small" />
      </ActionIconButton>
      <ActionIconButton size="small" title="Eliminar dispositivo" onClick={() => destroyRecord(record)}>
        <DeleteForeverOutlinedIcon fontSize="small" color="error" />
      </ActionIconButton>
    </Stack>
  );

  const columns = [
    {
      key: 'company',
      label: 'Empresa',
      render: (record) => {
        if (record?.platform_status === 'unregistered_metrics') return 'No registrado';
        return record?.company?.name || 'Sin empresa';
      },
    },
    {
      key: 'name',
      label: 'Nombre',
      render: (record) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{record?.name || (record?.platform_status === 'unregistered_metrics' ? 'Dispositivo no registrado' : '-')}</span>
          {!!record?.simulated && <Chip size="small" variant="outlined" label="Simulado" />}
          {!!record?.archived_at && <Chip size="small" variant="outlined" label="Archivado" title="Su analítica ya no existe en el equipo; conserva sus lecturas" />}
        </Stack>
      ),
    },
    {
      key: 'code',
      label: 'Código',
      render: (record) => record?.code || '-',
    },
    {
      key: 'kind',
      label: 'Tipo dispositivo',
      render: (record) => record?.kind || '-',
    },
    {
      key: 'store',
      label: 'Local',
      render: (record) => record?.store?.name || '-',
    },
    {
      key: 'zone',
      label: 'Zona',
      render: (record) => record?.zone || '-',
    },
    {
      key: 'device_type',
      label: 'Tipo sensor',
      render: (record) => _.compact([record?.device_type?.brand, record?.device_type?.model]).join(' ') || '-',
    },
    {
      key: 'metrics',
      label: 'Métricas',
      render: (record) => {
        if (record?.platform_status === 'unregistered_metrics') {
          return record?.metrics_count || 0;
        }
        return selectedDataFilter === 'without_metrics' ? '0' : '-';
      },
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => renderRowActions(record),
    },
  ];

  const handleCompanyFilterChange = (value) => {
    const nextCompanyId = value?.id || '';
    setCurrentPage(1);
    setSelectedCompanyId(nextCompanyId);
    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);
      if (nextCompanyId) {
        nextParams.set('company_id', nextCompanyId);
      } else {
        nextParams.delete('company_id');
      }
      return nextParams;
    }, { replace: true });
  };

  const handleDataFilterChange = (event) => {
    const nextDataFilter = event.target.value || '';
    setCurrentPage(1);
    setSelectedDataFilter(nextDataFilter);
    if (nextDataFilter === 'unregistered_metrics') {
      setSelectedCompanyId('');
    }

    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);
      if (nextDataFilter) {
        nextParams.set('data_filter', nextDataFilter);
      } else {
        nextParams.delete('data_filter');
      }

      if (nextDataFilter === 'unregistered_metrics') {
        nextParams.delete('company_id');
      }

      return nextParams;
    }, { replace: true });
  };

  return (
    <Box>
      <StandardPageHeader title="Superadministración · Dispositivos" />

      <StandardFiltersBar>
        <Grid container spacing={1.25} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={companies}
              getOptionLabel={(option) => option?.name || ''}
              value={selectedCompany}
              onChange={(e, value) => handleCompanyFilterChange(value)}
              disabled={selectedDataFilter === 'unregistered_metrics'}
              renderInput={(params) => <TextField {...params} label="Filtrar por empresa" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Filtro de datos"
              value={selectedDataFilter}
              onChange={handleDataFilterChange}
            >
              {DATA_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <StandardSearchField
              fullWidth
              placeholder="Buscar dispositivo"
              onChange={(e) => {
                setCurrentPage(1);
                setSearchQuery(e.target.value || '');
              }}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button variant="outlined" onClick={() => setOpenRegisterPhysical(true)}>
              Registrar equipo LoRaWAN
            </Button>
            <CreateButton onClick={openCreate}>
              Nuevo dispositivo
            </CreateButton>
          </Grid>
        </Grid>
      </StandardFiltersBar>

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            rows={records}
            rowKey="id"
            emptyMessage="No hay dispositivos para mostrar."
            mobileTitle={(record) => record?.name || '-'}
            mobileSubtitle={(record) => `${record?.company?.name || (record?.platform_status === 'unregistered_metrics' ? 'No registrado' : '-')} · ${record?.code || '-'}`}
            renderRowActions={(record) => renderRowActions(record)}
          />
        )}
      </Paper>

      <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageSelect={setCurrentPage} />

      <RegisterPhysicalDeviceDialog
        open={openRegisterPhysical}
        onClose={() => setOpenRegisterPhysical(false)}
        companies={companies}
        initialCompanyId={selectedCompanyId}
        onRegistered={loadRecords}
      />

      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? 'Editar dispositivo' : 'Nuevo dispositivo'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={companies}
                value={_.find(companies, (company) => `${company.id}` === `${form.company_id}`) || null}
                getOptionLabel={(option) => option?.name || ''}
                onChange={(e, value) => {
                  const companyId = value?.id || '';
                  setForm({ ...form, company_id: companyId, store_id: '', store: null });
                }}
                renderInput={(params) => <TextField {...params} required label="Empresa" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={editOptions?.stores || []}
                value={selectedStore}
                getOptionLabel={(option) => option?.name || ''}
                onChange={(e, value) => setForm({ ...form, store_id: value?.id || '', store: value || null })}
                renderInput={(params) => <TextField {...params} label="Local" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Código"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={editOptions?.device_types || []}
                value={selectedDeviceType}
                getOptionLabel={(option) => _.compact([option?.brand, option?.model]).join(' ')}
                onChange={(e, value) => setForm({ ...form, device_type_id: value?.id || '' })}
                renderInput={(params) => <TextField {...params} required label="Tipo de sensor" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={kinds}
                value={form.kind || ''}
                onInputChange={(e, value) => setForm({ ...form, kind: value || '' })}
                renderInput={(params) => <TextField {...params} label="Tipo de dispositivo" />}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={zones}
                value={form.zone || ''}
                onInputChange={(e, value) => setForm({ ...form, zone: value || '' })}
                renderInput={(params) => <TextField {...params} label="Zona" />}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!!form.simulated}
                    onChange={(e) => setForm({ ...form, simulated: e.target.checked })}
                  />
                )}
                label="Datos simulados (demos): el simulador le inventa lecturas cada minuto"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <SaveButton loading={saving} disabled={saving} onClick={saveRecord}>Guardar</SaveButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevicesIndex;
